import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task } from '../../../types';
import {
  calculateTaskProgress,
  formatHours,
  getTaskTotalCost,
  getTaskTotalTime,
} from '../../kanban/utils/calculations';
import { useKanbanStore } from '../../../store/useKanbanStore';

interface TaskCardProps {
  task: Task;
  onToggleSubtask: (taskId: string, subtaskId: string) => void;
  onClick: (task: Task) => void;
  canEditTasks?: boolean;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, onToggleSubtask, onClick, canEditTasks = true }) => {
  const showTime = useKanbanStore((state) => state.showTime);
  const showCost = useKanbanStore((state) => state.showCost);
  const showRawHours = useKanbanStore((state) => state.showRawHours);
  const addSubtaskNote = useKanbanStore((state) => state.addSubtaskNote);
  const deleteSubtaskNote = useKanbanStore((state) => state.deleteSubtaskNote);
  const addComment = useKanbanStore((state) => state.addComment);
  const deleteComment = useKanbanStore((state) => state.deleteComment);

  // Track active subtask note drawers
  const [activeSubtaskNoteId, setActiveSubtaskNoteId] = useState<string | null>(null);
  const [newNoteText, setNewNoteText] = useState('');
  
  // Track main card comments drawer and input state
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: task.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    touchAction: 'none',  
  };

  const progress = calculateTaskProgress(task.subtasks);
  const commentsList = task.comments || [];
  const hasAttachments = task.attachments && task.attachments.length > 0;
  const totalTime = getTaskTotalTime(task);
  const totalCost = getTaskTotalCost(task);
  const currencySymbol = task.cost?.currency === 'GBP' ? '£' : task.cost?.currency === 'EUR' ? '€' : '$';

  const dueDateDetails = task.dueDate
    ? (() => {
        const dueDate = new Date(task.dueDate);
        const dueDateKey = task.dueDate.slice(0, 10);
        const today = new Date();
        const todayKey = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, '0'), String(today.getDate()).padStart(2, '0')].join('-');
        const dueDay = new Date(`${dueDateKey}T00:00:00`);
        const todayStart = new Date(`${todayKey}T00:00:00`);
        const daysUntilDue = Math.round((dueDay.getTime() - todayStart.getTime()) / 86400000);
        const status = daysUntilDue < 0 ? 'overdue' : daysUntilDue === 0 ? 'today' : daysUntilDue <= 3 ? 'soon' : 'later';
        return {
          status,
          label: dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        } as const;
      })()
    : null;

  const priorityDetails = task.priority
    ? {
        high: { label: 'High', dot: 'bg-red-500', badge: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300' },
        medium: { label: 'Medium', dot: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300' },
        low: { label: 'Low', dot: 'bg-blue-500', badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300' },
      }[task.priority]
    : null;

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.closest('button') ||
      target.closest('label') ||
      target.closest('.subtask-notes-panel') ||
      target.closest('.task-comments-panel')
    ) {
      return;
    }

    if (typeof onClick === 'function') {
      onClick(task);
    }
  };

  const handleAddNote = (subtaskId: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteText.trim()) return;
    addSubtaskNote(task.id, subtaskId, newNoteText.trim());
    setNewNoteText('');
  };

  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCommentText.trim()) return;
    if (typeof addComment === 'function') {
      addComment(task.id, newCommentText.trim());
    }
    setNewCommentText('');
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(canEditTasks ? listeners : {})}
      onClick={handleClick}
      className={`p-2 sm:p-3 bg-white dark:bg-slate-800 rounded-md shadow-sm border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 ${canEditTasks ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'} space-y-2.5 transition-shadow`}
    >
      {/* Header: Title, Tags & Comments Button */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          {task.tags && task.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1.5">
              {task.tags.map((tag: any, idx: number) => {
                const label = typeof tag === 'string' ? tag : tag.label;
                const bgColor = typeof tag === 'object' && tag.color ? tag.color : '#64748b';
                const key = typeof tag === 'object' && tag.id ? tag.id : `${label}-${idx}`;

                return (
                  <span
                    key={key}
                    className="text-[10px] px-1.5 py-0.5 rounded font-medium text-white shadow-xs"
                    style={{ backgroundColor: bgColor }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          )}
          {priorityDetails && (
            <span className={`inline-flex items-center gap-1 mb-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${priorityDetails.badge}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${priorityDetails.dot}`} aria-hidden="true" />
              {priorityDetails.label}
            </span>
          )}
          <h4 className="font-medium text-sm text-slate-800 dark:text-slate-100 leading-snug break-words">{task.title}</h4>
        </div>

        {/* Main Card Comments Toggle Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsCommentsOpen(!isCommentsOpen);
            setNewCommentText('');
          }}
          className={`relative p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center gap-1 text-xs shrink-0 ${
            commentsList.length > 0 ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-slate-400 dark:text-slate-500'
          }`}
          title="View/Add task comments"
        >
          <span>💬</span>
          {commentsList.length > 0 && (
            <span className="bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-1 rounded-full text-[10px] font-bold">
              {commentsList.length}
            </span>
          )}
        </button>
      </div>

      {/* Expandable Comments Drawer */}
      {isCommentsOpen && (
        <div 
          className="task-comments-panel w-full min-w-0 max-w-full overflow-hidden bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded p-2.5 space-y-2 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-1.5">
            <span className="font-semibold text-slate-700 dark:text-slate-300 text-[11px]">Task Comments</span>
            <button
              type="button"
              onClick={() => setIsCommentsOpen(false)}
              className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 text-[10px]"
            >
              ✕ Close
            </button>
          </div>

          {/* List existing comments */}
          <div className="max-h-36 overflow-y-auto space-y-1.5">
            {commentsList.length === 0 ? (
              <p className="text-[11px] text-slate-400 dark:text-slate-500 italic">No comments added yet.</p>
            ) : (
              commentsList.map((comment: any) => (
                <div key={comment.id} className="bg-white dark:bg-slate-800 p-2 rounded border border-slate-200 dark:border-slate-700 flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-slate-400 dark:text-slate-500">
                      {comment.author ? `${comment.author} • ` : ''}
                      {new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {typeof deleteComment === 'function' && (
                      <button
                        type="button"
                        onClick={() => deleteComment(task.id, comment.id)}
                        className="text-slate-300 dark:text-slate-600 hover:text-rose-500 text-[10px]"
                        title="Delete comment"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                  <p className="text-slate-700 dark:text-slate-200 break-words">{comment.text}</p>
                </div>
              ))
            )}
          </div>

          {/* Add new comment form */}
          <form onSubmit={handleAddComment} className="flex gap-1 pt-1">
            <input
              type="text"
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              placeholder="Write a comment..."
              className="flex-1 px-2 py-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded text-[11px] text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
            <button
              type="submit"
              className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[11px] font-medium transition-colors"
            >
              Post
            </button>
          </form>
        </div>
      )}

      {/* Subtask Progress Bar */}
      {task.subtasks && task.subtasks.length > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] text-slate-500 dark:text-slate-400">
            <span>Subtasks</span>
            <span>
              {task.subtasks.filter((s) => s.completed).length}/{task.subtasks.length} ({progress}%)
            </span>
          </div>
          <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Subtask Items */}
          <div className="pt-1 space-y-1.5">
            {task.subtasks.map((st) => {
              const notesList = st.notes || [];
              const isNotesOpen = activeSubtaskNoteId === st.id;

              return (
                <div key={st.id} className="space-y-1">
                  <div 
                    className="flex min-w-0 items-center justify-between text-xs text-slate-600 dark:text-slate-300 bg-slate-50/60 dark:bg-slate-900/40 hover:bg-slate-100/80 dark:hover:bg-slate-900/80 px-2 py-1 rounded"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className="flex min-w-0 items-center gap-2 cursor-pointer flex-1 mr-1">
                      <input
                        type="checkbox"
                        checked={st.completed}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleSubtask(task.id, st.id);
                        }}
                        className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-0 dark:bg-slate-800"
                      />
                      <span className={`min-w-0 break-words ${st.completed ? 'line-through opacity-60' : ''}`}>{st.title}</span>
                      {(showTime && st.time && (st.time.estimatedHours > 0 || st.time.actualHours > 0)) ||
                        (showCost && st.cost && (st.cost.estimatedCost > 0 || st.cost.actualCost > 0)) ? (
                        <span className="text-[9px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
                          {showTime && st.time && (st.time.estimatedHours > 0 || st.time.actualHours > 0) && (
                            <>⏱ {formatHours(st.time.estimatedHours + st.time.actualHours, showRawHours)}</>
                          )}
                          {showCost && st.cost && (st.cost.estimatedCost > 0 || st.cost.actualCost > 0) && (
                            <> {currencySymbol} {(st.cost.estimatedCost + st.cost.actualCost).toLocaleString()}</>
                          )}
                        </span>
                      ) : null}
                    </label>

                    {/* Note Symbol Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSubtaskNoteId(isNotesOpen ? null : st.id);
                        setNewNoteText('');
                      }}
                      className={`relative p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center gap-0.5 text-[11px] ${
                        notesList.length > 0 ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-slate-400 dark:text-slate-500'
                      }`}
                      title="View/Add notes history"
                    >
                      <span>📝</span>
                      {notesList.length > 0 && (
                        <span className="bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-1 rounded-full text-[9px] font-bold">
                          {notesList.length}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Expandable Notes History Drawer */}
                  {isNotesOpen && (
                    <div 
                      className="subtask-notes-panel w-full max-w-full overflow-hidden bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded p-2 space-y-2 text-xs ml-5 mr-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-1">
                        <span className="font-semibold text-slate-700 dark:text-slate-300 text-[11px]">Notes History</span>
                        <button
                          type="button"
                          onClick={() => setActiveSubtaskNoteId(null)}
                          className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 text-[10px]"
                        >
                          ✕ Close
                        </button>
                      </div>

                      {/* List existing notes */}
                      <div className="max-h-28 overflow-y-auto space-y-1.5">
                        {notesList.length === 0 ? (
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 italic">No notes recorded yet.</p>
                        ) : (
                          notesList.map((note: any) => (
                            <div key={note.id} className="bg-white dark:bg-slate-800 p-1.5 rounded border border-slate-200 dark:border-slate-700 flex flex-col gap-0.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[9px] text-slate-400 dark:text-slate-500">
                                  {note.author ? `${note.author} • ` : ''}
                                  {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => deleteSubtaskNote(task.id, st.id, note.id)}
                                  className="text-slate-300 dark:text-slate-600 hover:text-rose-500 text-[10px]"
                                  title="Delete note"
                                >
                                  🗑️
                                </button>
                              </div>
                              <p className="text-slate-700 dark:text-slate-200 break-words">{note.text}</p>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Add new note input form */}
                      <form onSubmit={(e) => handleAddNote(st.id, e)} className="flex gap-1 pt-1">
                        <input
                          type="text"
                          value={newNoteText}
                          onChange={(e) => setNewNoteText(e.target.value)}
                          placeholder="Add a note..."
                          className="flex-1 px-2 py-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded text-[11px] text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                        <button
                          type="submit"
                          className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[11px] font-medium transition-colors"
                        >
                          Add
                        </button>
                      </form>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer: Assignee, Attachments, Cost, Time */}
      <div className="pt-2 border-t border-slate-100 dark:border-slate-700/60 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {task.assignee ? (
            <span className="font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">
              {task.assignee.name}
            </span>
          ) : <span />}

          {dueDateDetails && (
            <span
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium ${
                dueDateDetails.status === 'overdue'
                  ? 'bg-red-600 text-white'
                  : dueDateDetails.status === 'today'
                    ? 'bg-amber-400 text-amber-950'
                    : dueDateDetails.status === 'soon'
                      ? 'bg-yellow-100 text-amber-700 dark:bg-yellow-950/50 dark:text-amber-300'
                      : 'text-slate-500 dark:text-slate-400'
              }`}
              title={dueDateDetails.status === 'overdue' ? 'Overdue' : `Due ${dueDateDetails.label}`}
            >
              {dueDateDetails.status === 'overdue' ? '⚠️' : '📅'} {dueDateDetails.label}
            </span>
          )}

          {/* Attachments Indicator */}
          {hasAttachments && (
            <span 
              className="flex items-center gap-0.5 text-slate-500 dark:text-slate-400" 
              title={`${task.attachments?.length} attached file(s)`}
            >
              📎 {task.attachments?.length}
            </span>
          )}
        </div>

        <div className="flex max-w-full flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[10px]">
          {showTime && (task.time || totalTime.estimatedHours > 0 || totalTime.actualHours > 0) && (
            <span className="flex items-center gap-0.5">
              ⏱{' '}
              <span className="text-red-500 dark:text-red-400 font-medium">
                {formatHours(totalTime.estimatedHours, showRawHours)}
              </span>
              <span className="text-slate-300 dark:text-slate-600">/</span>
              <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                {formatHours(totalTime.actualHours, showRawHours)}
              </span>
            </span>
          )}
          
          {showCost && (task.cost || totalCost.estimatedCost > 0 || totalCost.actualCost > 0) && (
            <span className="flex items-center gap-0.5 font-semibold">
              <span className="text-slate-400 dark:text-slate-500 font-sans mr-0.5">
                {currencySymbol}
              </span>
              <span className="text-red-500 dark:text-red-400">
                {totalCost.estimatedCost}
              </span>
              <span className="text-slate-300 dark:text-slate-600 font-normal">/</span>
              <span className="text-emerald-600 dark:text-emerald-400">
                {totalCost.actualCost}
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};